import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Music, Settings, Loader2,
  Check, X, Trash2, Pin, Archive, BarChart3, CalendarDays, ShoppingBag, Megaphone, Zap, Radio, Star, TrendingUp, Shield, Ticket
} from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import TodaysRoomAdmin from '../components/TodaysRoomAdmin';
import StoreAdmin from '../components/StoreAdmin';
import SponsorAdmin from '../components/SponsorAdmin';
import PollsAdmin from '../components/PollsAdmin';
import AnalyticsAdmin from '../components/AnalyticsAdmin';
import ChatModerationAdmin from '../components/ChatModerationAdmin';
import VIPEventsAdmin from '../components/VIPEventsAdmin';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'anonymous', label: 'Anonymous', icon: MessageSquare },
  { id: 'music', label: 'Music Requests', icon: Music },
  { id: 'chat_mod', label: 'Chat Mod', icon: Shield },
  { id: 'room_actions', label: 'Room Actions', icon: Zap },
  { id: 'spotlights', label: 'Spotlights', icon: Star },
  { id: 'vip_events', label: 'VIP Events', icon: Ticket },
  { id: 'todays_room', label: "Today's Room", icon: CalendarDays },
  { id: 'store', label: 'Store', icon: ShoppingBag },
  { id: 'sponsor', label: 'Sponsor', icon: Megaphone },
  { id: 'polls', label: 'Polls', icon: BarChart3 },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  { id: 'webhooks', label: 'Webhooks', icon: Radio },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const QuickToggleBar = ({ token }) => {
  const [toggles, setToggles] = useState({ anonymous_enabled: true, music_requests_enabled: true, chat_enabled: true, spotlights_enabled: true });
  const [busy, setBusy] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/site-settings`).then(r => r.json()).then(s => {
      setToggles({ anonymous_enabled: s.anonymous_enabled ?? true, music_requests_enabled: s.music_requests_enabled ?? true, chat_enabled: s.chat_enabled ?? true, spotlights_enabled: s.spotlights_enabled ?? true });
    }).catch(() => {});
  }, []);

  const toggle = async (key) => {
    const newVal = !toggles[key];
    setBusy(key);
    setToggles(prev => ({ ...prev, [key]: newVal }));
    try {
      const params = new URLSearchParams();
      params.set(key, newVal);
      const res = await fetch(`${API}/api/admin/site-settings?${params}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const label = key === 'anonymous_enabled' ? 'Anonymous Messages' : key === 'chat_enabled' ? 'Live Chat' : key === 'spotlights_enabled' ? 'Spotlights' : 'Music Requests';
        toast.success(`${label}: ${newVal ? 'ON' : 'OFF'}`);
      } else {
        setToggles(prev => ({ ...prev, [key]: !newVal }));
        toast.error('Failed to update');
      }
    } catch {
      setToggles(prev => ({ ...prev, [key]: !newVal }));
      toast.error('Failed to update');
    }
    setBusy(null);
  };

  const ITEMS = [
    { key: 'anonymous_enabled', label: 'Anon', icon: MessageSquare, color: '#E91E8D' },
    { key: 'chat_enabled', label: 'Chat', icon: MessageSquare, color: '#10B981' },
    { key: 'music_requests_enabled', label: 'Music', icon: Music, color: '#8B5CF6' },
    { key: 'spotlights_enabled', label: 'Spots', icon: Star, color: '#F59E0B' },
  ];

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 z-50" data-testid="quick-toggle-bar">
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          className="w-10 h-10 rounded-full bg-black/80 backdrop-blur-xl border border-white/10 flex items-center justify-center shadow-lg shadow-black/50 hover:border-[#E91E8D]/30 transition-colors"
          data-testid="quick-toggle-expand"
          title="Quick Toggles"
        >
          <Zap size={14} className="text-[#E91E8D]" />
        </button>
      ) : (
        <div className="bg-black/90 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl shadow-black/60 p-3 min-w-[180px]">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">Quick Toggles</span>
            <button onClick={() => setCollapsed(true)} className="text-white/20 hover:text-white/50 transition-colors" data-testid="quick-toggle-collapse">
              <X size={12} />
            </button>
          </div>
          <div className="space-y-2">
            {ITEMS.map(item => {
              const on = toggles[item.key];
              const loading = busy === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => toggle(item.key)}
                  disabled={loading}
                  className="w-full flex items-center gap-2.5 group"
                  data-testid={`quick-toggle-${item.key}`}
                >
                  <item.icon size={13} style={{ color: on ? item.color : 'rgba(255,255,255,0.15)' }} className="transition-colors flex-shrink-0" />
                  <span className={`text-xs font-semibold flex-1 text-left transition-colors ${on ? 'text-white/70' : 'text-white/25'}`}>
                    {item.label}
                  </span>
                  <div className={`relative w-8 h-[18px] rounded-full transition-colors flex-shrink-0 ${on ? '' : 'bg-white/10'}`}
                    style={on ? { backgroundColor: `${item.color}30` } : {}}>
                    {loading && (
                      <Loader2 size={8} className="absolute top-[5px] left-1/2 -translate-x-1/2 animate-spin text-white/30" />
                    )}
                    <div className={`absolute top-[3px] w-3 h-3 rounded-full transition-all ${loading ? 'opacity-0' : ''}`}
                      style={{
                        left: on ? '16px' : '3px',
                        backgroundColor: on ? item.color : 'rgba(255,255,255,0.2)',
                      }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const token = localStorage.getItem('med_admin_token');
  const [activeTab, setActiveTab] = useState('anonymous');
  const [stats, setStats] = useState(null);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    const fetchStats = () => {
      fetch(`${API}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setStats).catch(() => {});
    };
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <div className="min-h-screen pt-20 pb-10 bg-black" data-testid="admin-page">
      <SEO title="Admin Panel" url="/admin" />
      <QuickToggleBar token={token} />
      <div className="flex">
        {/* Sidebar */}
        <div className="w-56 flex-shrink-0 border-r border-white/5 min-h-[calc(100vh-80px)] p-4 hidden md:block">
          <h2 className="text-white/20 text-[10px] font-bold tracking-widest uppercase mb-4">Admin Panel</h2>
          {stats && (
            <div className="glass-card p-3 mb-4 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Anon Pending</span>
                <span className="text-[#E91E8D] font-bold">{stats.anonymous?.pending || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Music Pending</span>
                <span className="text-[#8B5CF6] font-bold">{stats.music_requests?.pending || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Orders</span>
                <span className="text-green-400 font-bold">{stats.orders?.total || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Room Actions</span>
                <span className="text-[#EF4444] font-bold">{stats.room_actions?.active || 0}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Sponsors Pending</span>
                <span className="text-amber-400 font-bold">{stats.sponsor?.pending || 0}</span>
              </div>
            </div>
          )}
          <div className="space-y-1">
            {TABS.map(t => {
              let badgeCount = 0;
              let badgeColor = 'green';
              if (t.id === 'store') {
                badgeCount = stats?.orders?.pending || 0;
                badgeColor = 'green';
              } else if (t.id === 'sponsor') {
                badgeCount = stats?.sponsor?.pending || 0;
                badgeColor = 'amber';
              } else if (t.id === 'analytics') {
                badgeCount = alertCount;
                badgeColor = 'pink';
              }
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all text-left ${
                    activeTab === t.id
                      ? 'bg-[#E91E8D]/10 text-[#E91E8D] border border-[#E91E8D]/20'
                      : 'text-white/40 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`admin-tab-${t.id}`}
                >
                  <t.icon size={14} />
                  <span className="flex-1">{t.label}</span>
                  {badgeCount > 0 && (
                    <span className={`min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[9px] font-bold px-1 ${
                      badgeColor === 'amber' ? 'bg-amber-400/20 text-amber-400' : badgeColor === 'pink' ? 'bg-[#E91E8D]/20 text-[#E91E8D]' : 'bg-green-400/20 text-green-400'
                    }`}
                      data-testid={`${t.id}-badge`}>
                      {badgeCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {/* Mobile tabs */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-xl border-t border-white/5 flex">
          {TABS.map(t => {
            let badgeCount = 0;
            let badgeBg = 'bg-green-400';
            if (t.id === 'store') { badgeCount = stats?.orders?.pending || 0; }
            else if (t.id === 'sponsor') { badgeCount = stats?.sponsor?.pending || 0; badgeBg = 'bg-amber-400'; }
            else if (t.id === 'analytics') { badgeCount = alertCount; badgeBg = 'bg-[#E91E8D]'; }
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex-1 flex flex-col items-center gap-1 py-3 text-[9px] font-bold uppercase tracking-wider relative ${
                  activeTab === t.id ? 'text-[#E91E8D]' : 'text-white/30'
                }`}
              >
                <div className="relative">
                  <t.icon size={16} />
                  {badgeCount > 0 && (
                    <span className={`absolute -top-1.5 -right-2.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full ${badgeBg} text-black text-[7px] font-bold px-0.5`}
                      data-testid={`${t.id}-badge-mobile`}>
                      {badgeCount}
                    </span>
                  )}
                </div>
                {t.label}
              </button>
            );
          })}
        </div>
        {/* Content */}
        <div className="flex-1 p-4 sm:p-8 max-w-4xl">
          {activeTab === 'anonymous' && <AnonManager token={token} />}
          {activeTab === 'music' && <MusicManager token={token} />}
          {activeTab === 'chat_mod' && <ChatModerationAdmin token={token} />}
          {activeTab === 'room_actions' && <RoomActionsManager token={token} />}
          {activeTab === 'spotlights' && <SpotlightsManager token={token} />}
          {activeTab === 'vip_events' && <VIPEventsAdmin token={token} />}
          {activeTab === 'todays_room' && <TodaysRoomAdmin token={token} />}
          {activeTab === 'store' && <StoreAdmin token={token} />}
          {activeTab === 'sponsor' && <SponsorAdmin token={token} />}
          {activeTab === 'polls' && <PollsAdmin token={token} />}
          {activeTab === 'analytics' && <AnalyticsAdmin token={token} onAlertCount={setAlertCount} />}
          {activeTab === 'webhooks' && <WebhookMonitor token={token} />}
          {activeTab === 'settings' && <SettingsManager token={token} />}
        </div>
      </div>
    </div>
  );
};


const AnonManager = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    const params = filter ? `?status=${filter}` : '';
    fetch(`${API}/api/admin/anonymous${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setPosts(d); setLoading(false); }).catch(() => setLoading(false));
  }, [token, filter]);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/admin/anonymous/${id}/status?status=${status}`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` },
    });
    toast.success(status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : `Set to ${status}`);
    fetchPosts();
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this message permanently?')) return;
    await fetch(`${API}/api/admin/anonymous/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    toast.success('Deleted');
    fetchPosts();
  };

  const togglePin = async (id, pinned) => {
    await fetch(`${API}/api/admin/anonymous/${id}/pin?pinned=${!pinned}`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` },
    });
    fetchPosts();
  };

  return (
    <div data-testid="anonymous-manager">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <MessageSquare size={18} className="text-[#E91E8D]" /> Anonymous Messages
      </h2>
      <p className="text-white/30 text-xs mb-5">Review, approve, reject, archive, or delete submissions.</p>
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {['pending', 'approved', 'archived', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
              filter === s ? 'bg-[#E91E8D]/20 text-[#E91E8D] border border-[#E91E8D]/30' : 'bg-white/5 text-white/40 border border-white/10'
            }`}
            data-testid={`anon-filter-${s}`}
          >{s}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="text-[#E91E8D] animate-spin" /></div>
      ) : posts.length === 0 ? (
        <p className="text-white/20 text-sm text-center py-12">No {filter} messages.</p>
      ) : (
        <div className="space-y-2">
          {posts.map(p => (
            <div key={p.id} className="glass-card px-4 py-3" data-testid={`admin-anon-${p.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  {p.pinned && <span className="text-[#E91E8D] text-[9px] font-bold mr-1">PINNED</span>}
                  <p className="text-white text-sm mb-1">{p.content}</p>
                  <p className="text-white/20 text-[10px]">
                    {p.room_name && <span className="text-white/30 mr-2">Room: {p.room_name}</span>}
                    {new Date(p.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {p.status === 'pending' && (
                    <>
                      <button onClick={() => updateStatus(p.id, 'approved')} className="p-1.5 text-green-400 hover:bg-green-400/10 rounded" title="Approve" data-testid={`approve-${p.id}`}><Check size={14} /></button>
                      <button onClick={() => updateStatus(p.id, 'rejected')} className="p-1.5 text-red-400 hover:bg-red-400/10 rounded" title="Reject" data-testid={`reject-${p.id}`}><X size={14} /></button>
                    </>
                  )}
                  {p.status === 'approved' && (
                    <>
                      <button onClick={() => togglePin(p.id, p.pinned)} className="p-1.5 text-white/30 hover:text-[#E91E8D] hover:bg-[#E91E8D]/10 rounded" title="Pin"><Pin size={14} /></button>
                      <button onClick={() => updateStatus(p.id, 'archived')} className="p-1.5 text-white/30 hover:text-white hover:bg-white/5 rounded" title="Archive"><Archive size={14} /></button>
                    </>
                  )}
                  <button onClick={() => deletePost(p.id)} className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-400/10 rounded" title="Delete" data-testid={`delete-${p.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const MusicManager = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);

  const fetchRequests = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter) params.set('status', filter);
    params.set('sort_by', sortBy);
    fetch(`${API}/api/admin/music-requests?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setRequests(d); setLoading(false); }).catch(() => setLoading(false));
  }, [token, filter, sortBy]);
  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/admin/music-requests/${id}/status?status=${status}`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` },
    });
    toast.success(status === 'played' ? 'Marked as played' : `Set to ${status}`);
    fetchRequests();
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    await fetch(`${API}/api/admin/music-requests/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    toast.success('Deleted');
    fetchRequests();
  };

  return (
    <div data-testid="music-manager">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <Music size={18} className="text-[#8B5CF6]" /> Music Requests
      </h2>
      <p className="text-white/30 text-xs mb-5">View, approve, skip, play, or remove music requests.</p>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {['pending', 'approved', 'played', 'skipped', 'removed'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
              filter === s ? 'bg-[#8B5CF6]/20 text-[#8B5CF6] border border-[#8B5CF6]/30' : 'bg-white/5 text-white/40 border border-white/10'
            }`}
            data-testid={`music-filter-${s}`}
          >{s}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-white/20 text-[10px]">Sort:</span>
        {['newest', 'oldest', 'artist', 'song'].map(s => (
          <button key={s} onClick={() => setSortBy(s)}
            className={`px-2 py-1 rounded text-[10px] font-semibold capitalize ${
              sortBy === s ? 'text-white bg-white/10' : 'text-white/30 hover:text-white'
            }`}
          >{s}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="text-[#8B5CF6] animate-spin" /></div>
      ) : requests.length === 0 ? (
        <p className="text-white/20 text-sm text-center py-12">No {filter} requests.</p>
      ) : (
        <div className="space-y-2">
          {requests.map(r => (
            <div key={r.id} className="glass-card px-4 py-3" data-testid={`admin-music-${r.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{r.song_title} <span className="text-white/40">—</span> <span className="text-[#8B5CF6]">{r.artist_name}</span></p>
                  <p className="text-white/20 text-[10px] mt-0.5">
                    by {r.display_name || 'Anonymous'}
                    {r.message && <span className="text-white/30 ml-2">"{r.message}"</span>}
                    <span className="ml-2">{new Date(r.created_at).toLocaleString()}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {r.status === 'pending' && (
                    <>
                      <button onClick={() => updateStatus(r.id, 'approved')} className="p-1.5 text-green-400 hover:bg-green-400/10 rounded" title="Approve" data-testid={`approve-music-${r.id}`}><Check size={14} /></button>
                      <button onClick={() => updateStatus(r.id, 'skipped')} className="p-1.5 text-white/30 hover:bg-white/5 rounded" title="Skip"><X size={14} /></button>
                    </>
                  )}
                  {r.status === 'approved' && (
                    <>
                      <button onClick={() => updateStatus(r.id, 'played')} className="px-2 py-1 text-[#8B5CF6] text-[10px] font-bold hover:bg-[#8B5CF6]/10 rounded" data-testid={`play-music-${r.id}`}>PLAYED</button>
                      <button onClick={() => updateStatus(r.id, 'skipped')} className="px-2 py-1 text-white/30 text-[10px] hover:bg-white/5 rounded">SKIP</button>
                    </>
                  )}
                  <button onClick={() => deleteRequest(r.id)} className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-400/10 rounded" title="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const ACTION_TYPE_COLORS = {
  pop_your_up: '#EF4444',
  gu_them: '#F59E0B',
  one_mic_individual: '#3B82F6',
  one_mic_group: '#8B5CF6',
  come_back_goo: '#10B981',
  come_back_popped: '#EC4899',
};

const RoomActionsManager = ({ token }) => {
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);

  const fetchActions = useCallback(() => {
    setLoading(true);
    const params = filter ? `?status=${filter}` : '';
    fetch(`${API}/api/admin/room-actions${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setActions(d); setLoading(false); }).catch(() => setLoading(false));
  }, [token, filter]);
  useEffect(() => { fetchActions(); }, [fetchActions]);

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/admin/room-actions/${id}/status?status=${status}`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` },
    });
    toast.success(status === 'removed' ? 'Removed from ticker' : `Set to ${status}`);
    fetchActions();
  };

  const deleteAction = async (id) => {
    if (!window.confirm('Delete this room action permanently?')) return;
    await fetch(`${API}/api/admin/room-actions/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    toast.success('Deleted');
    fetchActions();
  };

  return (
    <div data-testid="room-actions-manager">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <Zap size={18} className="text-[#EF4444]" /> Room Actions
      </h2>
      <p className="text-white/30 text-xs mb-5">Manage paid room actions: Pop Your Opp, Gu Them, One Mic.</p>
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {['active', 'removed'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
              filter === s ? 'bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30' : 'bg-white/5 text-white/40 border border-white/10'
            }`}
            data-testid={`ra-filter-${s}`}
          >{s}</button>
        ))}
        <button onClick={() => setFilter('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            filter === '' ? 'bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30' : 'bg-white/5 text-white/40 border border-white/10'
          }`}
          data-testid="ra-filter-all"
        >All</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="text-[#EF4444] animate-spin" /></div>
      ) : actions.length === 0 ? (
        <p className="text-white/20 text-sm text-center py-12">No {filter || ''} room actions.</p>
      ) : (
        <div className="space-y-2">
          {actions.map(a => {
            const color = ACTION_TYPE_COLORS[a.action_type] || '#EF4444';
            const target = a.action_type === 'one_mic_group' ? a.target_group : a.target_name;
            return (
              <div key={a.id} className="glass-card px-4 py-3" data-testid={`admin-ra-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ color, backgroundColor: `${color}15` }}>
                        {a.action_label || a.action_type}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/30 uppercase font-semibold">{a.status}</span>
                    </div>
                    <p className="text-white text-sm font-medium">
                      Target: <span style={{ color }}>{target}</span>
                    </p>
                    <p className="text-white/20 text-[10px] mt-0.5">
                      {a.is_anonymous ? 'Anonymous' : (a.purchaser_name || 'No name')}
                      {a.message && <span className="text-white/30 ml-2">&ldquo;{a.message}&rdquo;</span>}
                      <span className="ml-2">{new Date(a.created_at).toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {a.status === 'active' && (
                      <button onClick={() => updateStatus(a.id, 'removed')} className="px-2 py-1 text-red-400 text-[10px] font-bold hover:bg-red-400/10 rounded" data-testid={`remove-ra-${a.id}`}>REMOVE</button>
                    )}
                    {a.status === 'removed' && (
                      <button onClick={() => updateStatus(a.id, 'active')} className="px-2 py-1 text-green-400 text-[10px] font-bold hover:bg-green-400/10 rounded" data-testid={`reactivate-ra-${a.id}`}>REACTIVATE</button>
                    )}
                    <button onClick={() => deleteAction(a.id)} className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-400/10 rounded" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const EVENT_TYPE_STYLES = {
  'payment.created': { color: '#60a5fa', label: 'Payment Created' },
  'payment.updated': { color: '#34d399', label: 'Payment Updated' },
  'refund.created': { color: '#f87171', label: 'Refund Created' },
  'refund.updated': { color: '#fb923c', label: 'Refund Updated' },
};

const WebhookMonitor = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/webhook-events?limit=50`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setEvents(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const formatAmount = (cents, currency) => {
    if (!cents) return null;
    return `${currency || 'USD'} ${(cents / 100).toFixed(2)}`;
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div data-testid="webhook-monitor">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <Radio size={18} className="text-green-400" /> Webhook Events
          </h2>
          <p className="text-white/30 text-xs mt-0.5">Real-time Square payment event log.</p>
        </div>
        <button
          onClick={fetchEvents}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/40 hover:text-white hover:bg-white/10 text-[10px] font-semibold transition-colors disabled:opacity-40"
          data-testid="refresh-webhooks-btn"
        >
          {loading ? <Loader2 size={10} className="animate-spin" /> : <Radio size={10} />} Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="text-green-400 animate-spin" /></div>
      ) : events.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="no-webhook-events">
          <Radio size={28} className="text-white/10 mx-auto mb-3" />
          <p className="text-white/25 text-sm font-medium">No webhook events yet</p>
          <p className="text-white/15 text-xs mt-1">Events will appear here when Square sends payment notifications.</p>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="webhook-events-list">
          {events.map((ev, i) => {
            const style = EVENT_TYPE_STYLES[ev.event_type] || { color: '#94a3b8', label: ev.event_type };
            const amount = formatAmount(ev.amount_cents, ev.currency);
            return (
              <div key={ev.event_id || i} className="glass-card px-4 py-3" data-testid={`webhook-event-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: style.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase" style={{ color: style.color }}>{style.label}</span>
                      {ev.payment_status && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase ${
                          ev.payment_status === 'COMPLETED' ? 'bg-green-400/10 text-green-400' :
                          ev.payment_status === 'FAILED' ? 'bg-red-400/10 text-red-400' :
                          'bg-white/5 text-white/30'
                        }`}>{ev.payment_status}</span>
                      )}
                      {ev.refund_status && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-400/10 text-red-400 font-semibold uppercase">{ev.refund_status}</span>
                      )}
                      {amount && <span className="text-white/50 text-[10px] font-medium">{amount}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[9px] text-white/20">
                      {ev.order_id && <span>Order: <span className="text-white/30 font-mono">{ev.order_id.slice(0, 16)}...</span></span>}
                      {ev.payment_id && <span>Pay: <span className="text-white/30 font-mono">{ev.payment_id.slice(0, 16)}...</span></span>}
                      {ev.refund_id && <span>Refund: <span className="text-white/30 font-mono">{ev.refund_id.slice(0, 16)}...</span></span>}
                    </div>
                  </div>
                  <span className="text-white/15 text-[9px] flex-shrink-0 whitespace-nowrap">{timeAgo(ev.processed_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


/* ═══════ Spotlights Manager ═══════ */
const SpotlightsManager = ({ token }) => {
  const [spotlights, setSpotlights] = useState([]);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [configured, setConfigured] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const r = await fetch(`${API}/api/admin/spotlights${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setSpotlights(await r.json());
    } catch {}
    setLoading(false);
  }, [token, filter]);

  const checkConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/spotlights/packages`);
      if (r.ok) {
        const d = await r.json();
        setConfigured(d.configured);
      }
    } catch {}
  }, []);

  useEffect(() => { load(); checkConfig(); }, [load, checkConfig]);

  const setupCatalog = async () => {
    setSetupLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/spotlights/setup-catalog`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        toast.success('Spotlight catalog created in Square!');
        setConfigured(true);
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Setup failed');
      }
    } catch { toast.error('Setup failed'); }
    setSetupLoading(false);
  };

  const endSpotlight = async (id) => {
    try {
      const r = await fetch(`${API}/api/admin/spotlights/${id}/end`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) { toast.success('Spotlight ended'); load(); }
      else toast.error('Failed to end spotlight');
    } catch { toast.error('Failed'); }
  };

  const hideSpotlight = async (id) => {
    try {
      const r = await fetch(`${API}/api/admin/spotlights/${id}/hide`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) { toast.success('Spotlight hidden'); load(); }
      else toast.error('Failed to hide');
    } catch { toast.error('Failed'); }
  };

  const STATUS_LABELS = {
    active: { bg: 'bg-green-400/10 text-green-400', text: 'Active' },
    expired: { bg: 'bg-white/5 text-white/40', text: 'Expired' },
    ended: { bg: 'bg-amber-400/10 text-amber-400', text: 'Ended Early' },
    hidden: { bg: 'bg-red-400/10 text-red-400', text: 'Hidden' },
    failed: { bg: 'bg-red-400/10 text-red-400', text: 'Failed' },
    revoked: { bg: 'bg-red-400/10 text-red-400', text: 'Revoked' },
  };

  const FILTERS = ['active', 'expired', 'ended', 'hidden', ''];
  const FILTER_LABELS = { active: 'Active', expired: 'Expired', ended: 'Ended', hidden: 'Hidden', '': 'All' };

  return (
    <div data-testid="spotlights-manager">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <Star size={18} className="text-[#E91E8D]" /> Spotlights
      </h2>
      <p className="text-white/30 text-xs mb-4">Manage paid spotlight placements in the room ticker.</p>

      {configured === false && (
        <div className="glass-card p-5 mb-4 border-amber-400/20">
          <p className="text-amber-400 text-sm font-semibold mb-2">Setup Required</p>
          <p className="text-white/40 text-xs mb-3">Create Spotlight products in your Square catalog before users can purchase.</p>
          <button onClick={setupCatalog} disabled={setupLoading} className="btn-pink !text-xs" data-testid="setup-catalog-btn">
            {setupLoading ? <Loader2 size={14} className="animate-spin" /> : null}
            {setupLoading ? 'Creating...' : 'Setup Spotlight Catalog'}
          </button>
        </div>
      )}

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
              filter === f ? 'bg-[#E91E8D]/10 text-[#E91E8D] border-[#E91E8D]/30' : 'bg-white/5 text-white/40 border-white/10'
            }`}
            data-testid={`filter-${f || 'all'}`}
          >{FILTER_LABELS[f]}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 text-[#E91E8D] animate-spin mx-auto" /></div>
      ) : spotlights.length === 0 ? (
        <div className="text-center py-10 text-white/20 text-sm">No spotlights found</div>
      ) : (
        <div className="space-y-2">
          {spotlights.map(s => {
            const st = STATUS_LABELS[s.status] || { bg: 'bg-white/5 text-white/40', text: s.status };
            return (
              <div key={s.id} className="glass-card p-4" data-testid={`spotlight-admin-${s.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${st.bg}`}>{st.text}</span>
                    <span className="text-white/20 text-[9px]">{s.package} &bull; ${(s.amount_cents / 100).toFixed(0)}</span>
                  </div>
                  <div className="flex gap-1">
                    {s.status === 'active' && (
                      <button onClick={() => endSpotlight(s.id)} className="px-2 py-1 rounded text-[9px] bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 transition-colors" data-testid={`end-${s.id}`}>End Early</button>
                    )}
                    {['active', 'ended', 'expired'].includes(s.status) && (
                      <button onClick={() => hideSpotlight(s.id)} className="px-2 py-1 rounded text-[9px] bg-red-400/10 text-red-400 hover:bg-red-400/20 transition-colors" data-testid={`hide-${s.id}`}>Hide</button>
                    )}
                  </div>
                </div>
                <p className="text-white/60 text-xs font-medium mb-1">
                  {s.is_anonymous ? 'Anonymous' : (s.display_name || 'Anonymous')}
                </p>
                <p className="text-white/40 text-xs mb-2 break-words">{s.message}</p>
                {s.link_url && <p className="text-[#E91E8D]/50 text-[10px] mb-2 truncate">{s.link_url}</p>}
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-white/15 text-[9px]">
                  {s.activated_at && <span>Activated: {new Date(s.activated_at).toLocaleString()}</span>}
                  {s.spotlight_expires_at && <span>Expires: {new Date(s.spotlight_expires_at).toLocaleString()}</span>}
                  {s.square_order_id && <span>Order: {s.square_order_id.slice(0, 12)}...</span>}
                  <span>Created: {new Date(s.created_at).toLocaleString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SettingsManager = ({ token }) => {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/site-settings`).then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.set('anonymous_enabled', settings.anonymous_enabled);
      params.set('music_requests_enabled', settings.music_requests_enabled);
      params.set('chat_enabled', settings.chat_enabled ?? true);
      if (settings.anonymous_display_mode) params.set('anonymous_display_mode', settings.anonymous_display_mode);
      if (settings.music_display_mode) params.set('music_display_mode', settings.music_display_mode);
      if (settings.listlive_image_url !== undefined) params.set('listlive_image_url', settings.listlive_image_url);
      if (settings.listlive_appstore_url) params.set('listlive_appstore_url', settings.listlive_appstore_url);
      if (settings.listlive_playstore_url) params.set('listlive_playstore_url', settings.listlive_playstore_url);
      const res = await fetch(`${API}/api/admin/site-settings?${params}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSettings(await res.json());
        toast.success('Settings saved');
      } else throw new Error();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div data-testid="settings-manager">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <Settings size={18} className="text-white/40" /> Settings
      </h2>
      <p className="text-white/30 text-xs mb-6">Enable/disable features and manage ListLive links.</p>

      <div className="space-y-6">
        <div className="glass-card p-5">
          <h3 className="text-white text-sm font-bold mb-4">Feature Toggles</h3>
          <div className="space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-white/60 text-sm">Anonymous Messages</span>
              <input type="checkbox" checked={settings.anonymous_enabled ?? true}
                onChange={e => setSettings(s => ({ ...s, anonymous_enabled: e.target.checked }))}
                className="w-4 h-4 accent-[#E91E8D]" data-testid="toggle-anonymous" />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-white/60 text-sm">Music Requests</span>
              <input type="checkbox" checked={settings.music_requests_enabled ?? true}
                onChange={e => setSettings(s => ({ ...s, music_requests_enabled: e.target.checked }))}
                className="w-4 h-4 accent-[#8B5CF6]" data-testid="toggle-music" />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-white/60 text-sm">Live Chat</span>
              <input type="checkbox" checked={settings.chat_enabled ?? true}
                onChange={e => setSettings(s => ({ ...s, chat_enabled: e.target.checked }))}
                className="w-4 h-4 accent-[#10B981]" data-testid="toggle-chat" />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-white/60 text-sm">Spotlights</span>
              <input type="checkbox" checked={settings.spotlights_enabled ?? true}
                onChange={e => setSettings(s => ({ ...s, spotlights_enabled: e.target.checked }))}
                className="w-4 h-4 accent-[#F59E0B]" data-testid="toggle-spotlights" />
            </label>
          </div>
        </div>
        <div className="glass-card p-5">
          <h3 className="text-white text-sm font-bold mb-4">Display Modes</h3>
          <p className="text-white/30 text-[10px] mb-4">Control whether submissions appear instantly or require admin approval first.</p>
          <div className="space-y-4">
            <div>
              <label className="text-white/60 text-sm block mb-2">Anonymous Messages</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setSettings(s => ({ ...s, anonymous_display_mode: 'immediate' }))}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                    (settings.anonymous_display_mode || 'immediate') === 'immediate'
                      ? 'bg-[#E91E8D]/10 text-[#E91E8D] border-[#E91E8D]/30'
                      : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20'
                  }`}
                  data-testid="anon-mode-immediate"
                >
                  Immediate
                </button>
                <button
                  onClick={() => setSettings(s => ({ ...s, anonymous_display_mode: 'approval_required' }))}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                    settings.anonymous_display_mode === 'approval_required'
                      ? 'bg-[#E91E8D]/10 text-[#E91E8D] border-[#E91E8D]/30'
                      : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20'
                  }`}
                  data-testid="anon-mode-approval"
                >
                  Admin Approval
                </button>
              </div>
            </div>
            <div>
              <label className="text-white/60 text-sm block mb-2">Music Requests</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setSettings(s => ({ ...s, music_display_mode: 'immediate' }))}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                    (settings.music_display_mode || 'immediate') === 'immediate'
                      ? 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30'
                      : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20'
                  }`}
                  data-testid="music-mode-immediate"
                >
                  Immediate
                </button>
                <button
                  onClick={() => setSettings(s => ({ ...s, music_display_mode: 'approval_required' }))}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                    settings.music_display_mode === 'approval_required'
                      ? 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30'
                      : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20'
                  }`}
                  data-testid="music-mode-approval"
                >
                  Admin Approval
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="glass-card p-5">
          <h3 className="text-white text-sm font-bold mb-4">ListLive Section</h3>
          <div className="space-y-3">
            <div>
              <label className="text-white/40 text-xs block mb-1">Image URL</label>
              <input type="text" value={settings.listlive_image_url || ''} onChange={e => setSettings(s => ({ ...s, listlive_image_url: e.target.value }))}
                className="input-med w-full !py-2.5 !text-sm" placeholder="https://..." data-testid="listlive-image-url" />
            </div>
            <div>
              <label className="text-white/40 text-xs block mb-1">App Store URL</label>
              <input type="text" value={settings.listlive_appstore_url || ''} onChange={e => setSettings(s => ({ ...s, listlive_appstore_url: e.target.value }))}
                className="input-med w-full !py-2.5 !text-sm" data-testid="listlive-appstore-input" />
            </div>
            <div>
              <label className="text-white/40 text-xs block mb-1">Google Play URL</label>
              <input type="text" value={settings.listlive_playstore_url || ''} onChange={e => setSettings(s => ({ ...s, listlive_playstore_url: e.target.value }))}
                className="input-med w-full !py-2.5 !text-sm" data-testid="listlive-playstore-input" />
            </div>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-pink !text-xs" data-testid="save-settings">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AdminPage;
